// Module Import
//---------------------------------
import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Link } from 'gatsby-link'
import { StaticImage } from 'gatsby-plugin-image'
import TypographyAtom from '../../atoms/typography/typography'
import Image from '../../atoms/images/images'
import { ROUTES } from '../../../constants/routes'
import { brandGrey, brandIvory, brandKhaki, brandOrange } from '../../../assets/consts/color'
import ArticleDate from '../ArticleDate/ArticleDate'
import YouTubeIcon from '../../atoms/icons/YouTubeIcon'
import EmbeddedYouTubeVideo from '../EmbeddedYouTubeVideo/EmbeddedYouTubeVideo'

// import YouTubeIcon from '../../../assets/images/youtube_icon.svg'

// Component Import
//---------------------------------

// Styled Component Declarations
//---------------------------------
const ImageFilter = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 2;
  background-color: ${brandOrange};
  border-radius: 4px;
  mix-blend-mode: overlay;
  opacity: 0;
  transition: 0.25s;
  /* transform: scale(99.2%); */
`

const ImageWrapper = styled.div`
  position: relative;

  img,
  .gatsby-image-wrapper img {
    transition: 0.25s;
    filter: grayscale(0%);
  }

  &.placeholder {
    background-color: #feece6;
  }
`

const BlogArticleCardWrapper = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  text-decoration: none;
  transition: 0.25s;

  @media only screen and (min-width: 64em) {
    gap: 24px;
  }

  &:hover ${ImageFilter} {
    opacity: 1;
  }
  &:hover ${ImageWrapper} {
    img,
    .gatsby-image-wrapper img {
      filter: grayscale(100%);
      transform: scale(105%);
    }
    &.placeholder .gatsby-image-wrapper img {
      filter: none;
    }
  }

  &:hover video,
  &:hover .video {
    transform: scale(110%);
  }

  &.row-direction {
    flex-direction: row;

    .col-xs-6 {
      padding: 0;
      flex-shrink: 1;
    }
  }

  &.snippet-row {
    @media only screen and (min-width: 90em) {
      flex-direction: row;

      .col-lg-5,
      .col-lg-7 {
        padding: 0;
        flex-shrink: 1;
      }
    }
  }
`

const CopyWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 8px;

  @media only screen and (min-width: 64em) {
    gap: 12px;
  }

  .short-description {
    color: ${brandGrey};
    font-size: 18px;
  }

  .reduced-title-size {
    font-size: 20px;
  }

  .reduced-descr-size {
    font-size: 16px;
  }

  .snippet-title-lg {
    @media only screen and (min-width: 90em) {
      font-size: 18px;
      margin-bottom: -4px;
    }
  }

  h4,
  h5 {
    margin: 0;
  }

  h5 {
    color: #fb7347;
    margin: 0;
    font-size: 18px;
  }

  h4 {
    font-size: 24px;
  }
`
const StyledYouTubeIcon = styled(YouTubeIcon)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(136%);
  transition: 0.25s;
`

const VideoWrapper = styled.div`
  position: relative;
  overflow: hidden;

  video,
  .video {
    transition: 0.25s;
    transform: scale(103%);
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    display: block;
    width: 100%;
    height: 100%;
  }

  &:hover ${ImageFilter} {
    opacity: 1;
  }

  &:hover video,
  &:hover .video {
    transform: scale(110%);
  }

  &:hover ${StyledYouTubeIcon} {
    transform: translate(-50%, -50%) scale(141%);
  }
`

const CategoryLabel = styled.div`
  flex-shrink: 0;
  flex-grow: 0;
  margin-top: auto;
  padding: 4px 10px;

  background-color: ${brandIvory};

  p {
    color: ${brandKhaki};
  }
`

// Component Export
//---------------------------------
export default function BlogArticleCard({
  snippet,
  row,
  reducedSize,
  articleCategory,
  header,
  excerpt,
  altTag,
  imgSrc,
  url,
  linkDestination,
  link = '',
  articleShortDescription,
  articleAuthor,
  updatedDate,
  publishedDate,
  snippetYouTubeLink,
  snippetVideoFile,
  nonCategory,
}) {
  return (
    <BlogArticleCardWrapper
      to={link || `${ROUTES.BLOG}/${linkDestination}`}
      className={`${row ? 'row-direction' : ''} ${snippet ? 'snippet-row' : ''}`}
    >
      <div className={`${row ? 'col-xs-6' : ''} ${snippet ? 'col-lg-5' : ''}`}>
        {!snippetVideoFile && !imgSrc && !url && !snippetYouTubeLink && (
          <ImageWrapper className="placeholder">
            <StaticImage
              src="../../../assets/images/placeholder_image_article.svg"
              alt="No Image"
              width={600}
              height={337.5}
            />
            <ImageFilter />
          </ImageWrapper>
        )}
        {(imgSrc || url) && (
          <ImageWrapper>
            <Image
              cutSize={snippet || reducedSize}
              imgSrc={imgSrc}
              url={url}
              altTag={altTag}
              hasFrame="Left Clip"
              aspectRatio="16/9"
            />
            <ImageFilter />
          </ImageWrapper>
        )}
        {snippetVideoFile && !imgSrc && !url && !snippetYouTubeLink && (
          <VideoWrapper>
            <video width="100%" height="100%" controls={false} muted>
              <source src={snippetVideoFile} type="video/webm" />
            </video>
            <ImageFilter />
            <StyledYouTubeIcon />
          </VideoWrapper>
        )}
        {snippetYouTubeLink && !imgSrc && !url && !snippetVideoFile && (
          <VideoWrapper>
            <EmbeddedYouTubeVideo youTubeLink={snippetYouTubeLink} />
            <ImageFilter />
          </VideoWrapper>
        )}
      </div>
      <CopyWrapper className={`${row && 'col-xs-6'} ${snippet && 'col-lg-7'}`}>
        {/* ADD IF NEEDED */}
        {/* <ArticleDate
          inCard
          reducedSize={reducedSize}
          updatedDate={updatedDate}
          publishedDate={publishedDate}
          updatedDateLabel="Updated on"
          publishedDateLabel="Published on"
          articleAuthor={articleAuthor}
        /> */}
        <TypographyAtom
          className={`${reducedSize && 'reduced-title-size'} ${snippet && 'snippet-title-lg'}`}
          type="H4"
          copy={header}
          weight="Bold"
        />
        {articleShortDescription && (
          <TypographyAtom
            className={`short-description ${reducedSize && 'reduced-descr-size'}`}
            type="P"
            copy={articleShortDescription}
            weight="Regular"
          />
        )}
        {!nonCategory && (
          <CategoryLabel>
            <TypographyAtom type="P" copy={articleCategory} weight="Regular" />
          </CategoryLabel>
        )}
      </CopyWrapper>
    </BlogArticleCardWrapper>
  )
}

BlogArticleCard.propTypes = {
  articleCategory: PropTypes.string.isRequired,
  header: PropTypes.string.isRequired,
  articleShortDescription: PropTypes.string.isRequired,
  altTag: PropTypes.string.isRequired,
  imgSrc: PropTypes.string.isRequired,
  linkDestination: PropTypes.string.isRequired,
  link: PropTypes.string,
}
